import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "rf_cookie_consent_v1";

/**
 * Minimal GDPR cookie banner. Stores accept/reject in localStorage so it
 * only shows once. We use one strictly-necessary cookie (the auth JWT in
 * localStorage), so the banner doubles as awareness rather than a blocker.
 */
export default function CookieBanner() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!localStorage.getItem(STORAGE_KEY)) {
            // Slight delay so it doesn't compete with the initial hero animation
            const t = setTimeout(() => setVisible(true), 800);
            return () => clearTimeout(t);
        }
    }, []);

    const close = (decision) => {
        localStorage.setItem(STORAGE_KEY, decision);
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            data-testid="cookie-banner"
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-50 bg-white border border-line shadow-lg p-5"
        >
            <div className="flex items-start gap-3">
                <Cookie className="w-5 h-5 text-klein mt-0.5 flex-shrink-0" />
                <div className="text-sm leading-relaxed">
                    <p className="mb-3">
                        We use a single strictly-necessary cookie to keep you signed in.
                        No tracking, no ads. See our{" "}
                        <Link to="/privacy" className="underline text-klein hover:text-kleinDark">
                            privacy policy
                        </Link>{" "}
                        for details.
                    </p>
                    <div className="flex gap-2">
                        <button
                            data-testid="cookie-accept"
                            onClick={() => close("accepted")}
                            className="px-4 py-2 text-xs bg-klein text-white hover:bg-kleinDark"
                        >
                            Accept
                        </button>
                        <button
                            data-testid="cookie-reject"
                            onClick={() => close("rejected")}
                            className="px-4 py-2 text-xs border border-line hover:bg-paperWarm"
                        >
                            Decline
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
